import { useEffect, useRef, useState } from "react";
import { Link2, Check, AlertCircle } from "lucide-react";
import { ensureStandingInviteLink } from "./inviteCardsApi.js";
import { ensureStandingEmployerInviteLink } from "./employerInviteCardsApi.js";
import { copyTextToClipboard } from "./clipboard.js";

const RESET_MS = 4000;


const VARIANTS = {
  worker: {
    fetchLink: ensureStandingInviteLink,
    idleText: "คัดลอกลิงก์เชิญ",
    describe: (name) => (name ? `คัดลอกลิงก์เชิญแรงงานของ ${name}` : "คัดลอกลิงก์เชิญแรงงาน"),
    forbidden: "คุณไม่มีสิทธิ์ออกลิงก์เชิญแทน Agent รายนี้",
  },
  employer: {
    fetchLink: ensureStandingEmployerInviteLink,
    idleText: "คัดลอกลิงก์เชิญนายจ้าง",
    describe: (name) => (name ? `คัดลอกลิงก์เชิญนายจ้างของ ${name}` : "คัดลอกลิงก์เชิญนายจ้าง"),
    forbidden: "คุณไม่มีสิทธิ์ออกลิงก์เชิญนายจ้างแทน Agent รายนี้",
  },
};

/**
 * "Copy this Agent's invite link" as a single button.
 *
 * THE POINT IS THE MISSING STEP. Sending a worker an invite used to mean: open
 * the Agent's Invite Card screen, fill in the create form (usage cap, expiry,
 * note, country, language), save, then copy. A SuperAdmin sending on an
 * Agent's behalf has no basis for any of those choices. This button collapses
 * all of it into one press by asking the backend for the Agent's ONE standing
 * link, which it creates on first use and returns unchanged forever after - so
 * pressing it twice, or next month, or as a different operator, yields the
 * same URL. Nothing here caches: the endpoint itself is the idempotent seam.
 *
 * TWO AUDIENCES, ONE BUTTON. A worker-side Agent (ฝั่งแรงงาน) invites workers;
 * a job-side Agent (ฝั่งงาน) invites EMPLOYERS and has no worker pipeline at
 * all - the worker endpoint answers WrongOrganizationSide for it. So the
 * variant decides which standing link is fetched and how the button reads;
 * everything else about the interaction is identical, because to the operator
 * it is the same gesture.
 *
 * NEVER FAILS SILENTLY. The live backoffice is served over plain HTTP, where
 * navigator.clipboard.writeText can be blocked outright; copyTextToClipboard
 * already falls back to execCommand, but when even that fails this renders the
 * real URL in a focused, selected, read-only input so the operator can copy it
 * by hand. A button that says nothing after a blocked copy would have the
 * operator sending an empty message.
 */
export default function CopyInviteLinkButton({ agentId, agentDisplayName, variant = "worker", className = "" }) {
  const config = VARIANTS[variant] ?? VARIANTS.worker;
  // "idle" | "working" | "copied" | "manual" | "error"
  const [state, setState] = useState("idle");
  const [url, setUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const manualInputRef = useRef(null);
  const resetTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(resetTimerRef.current), []);

  useEffect(() => {
    if (state === "manual") {
      manualInputRef.current?.focus();
      manualInputRef.current?.select();
    }
  }, [state]);

  function scheduleReset() {
    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => setState("idle"), RESET_MS);
  }

  async function handleClick(event) {
    // The Agents list opens the detail drawer on row click; copying a link is
    // not a request to leave the row.
    event.stopPropagation();
    if (state === "working") {
      return;
    }

    clearTimeout(resetTimerRef.current);
    setState("working");
    setErrorMessage(null);

    let card;
    try {
      card = await config.fetchLink(agentId);
    } catch (error) {
      setErrorMessage(resolveErrorMessage(error, config));
      setState("error");
      scheduleReset();
      return;
    }

    const inviteUrl = card?.inviteUrl;
    if (!inviteUrl) {
      // The server answered but withheld the link - a real permission/config
      // outcome, not something to paper over with a fake success.
      setErrorMessage("เซิร์ฟเวอร์ไม่ได้ส่งลิงก์กลับมา - อาจไม่มีสิทธิ์ดูลิงก์ของ Agent รายนี้");
      setState("error");
      scheduleReset();
      return;
    }

    setUrl(inviteUrl);
    const copied = await copyTextToClipboard(inviteUrl);
    setState(copied ? "copied" : "manual");
    if (copied) {
      scheduleReset();
    }
  }

  const label = config.describe(agentDisplayName);

  return (
    <>
      <button
        type="button"
        className={`wx-copy-invite ${stateModifier(state)} ${className}`.replace(/\s+/g, " ").trim()}
        onClick={handleClick}
        disabled={state === "working"}
        aria-label={label}
      >
        {state === "copied" ? <Check size={14} aria-hidden="true" /> : null}
        {state === "error" ? <AlertCircle size={14} aria-hidden="true" /> : null}
        {state !== "copied" && state !== "error" ? <Link2 size={14} aria-hidden="true" /> : null}
        <span>{buttonText(state, config)}</span>
      </button>

      {state === "manual" && url ? (
        <div className="wx-copy-invite-manual" onClick={(event) => event.stopPropagation()}>
          <p className="wx-copy-invite-manual__note">คัดลอกอัตโนมัติไม่สำเร็จ กด Ctrl+C เพื่อคัดลอกลิงก์นี้</p>
          <input ref={manualInputRef} type="text" readOnly value={url} className="wx-copy-invite-manual__url" aria-label="ลิงก์เชิญ" />
        </div>
      ) : null}

      {state === "error" && errorMessage ? (
        <p className="wx-copy-invite-error" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </>
  );
}

function stateModifier(state) {
  if (state === "copied") return "wx-copy-invite--copied";
  if (state === "error") return "wx-copy-invite--error";
  return "";
}

function buttonText(state, config) {
  switch (state) {
    case "working":
      return "กำลังเตรียมลิงก์...";
    case "copied":
      return "คัดลอกแล้ว";
    case "manual":
      return "คัดลอกเองด้านล่าง";
    case "error":
      return "ไม่สำเร็จ";
    default:
      return config.idleText;
  }
}

/**
 * Surfaces what the server actually said. The two outcomes an operator can act
 * on get their own sentence; anything else keeps the server's own message
 * rather than replacing it with a generic one that hides the cause.
 */
function resolveErrorMessage(error, config) {
  if (error?.status === 403) {
    return config.forbidden;
  }
  if (error?.status === 404) {
    return "ไม่พบ Agent รายนี้";
  }
  return error?.body?.message || error?.message || "ไม่สามารถเตรียมลิงก์เชิญได้ กรุณาลองใหม่อีกครั้ง";
}
